import React, { useEffect, useMemo } from 'react'
import { RouteComponentProps, withRouter } from 'react-router'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferPage from 'components/OfferPage'
import BedbankOfferPage from 'components/Pages/BedbankOfferPage'
import Error404Page from 'components/Pages/Error404Page'
import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchBestOfferForProperty, fetchOfferById } from 'actions/OfferActions'
import { detectOfferTypeFromId } from 'lib/offer/offerUtils'
import { connect } from 'react-redux'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import BodyText from 'components/Luxkit/Typography/BodyText'
import HttpStatus from 'components/Common/HttpStatus'
import OfferPageHeadData from 'components/OfferPage/OfferPageHeadData'
import HeadData from 'components/Common/HeadData'
import config from 'constants/config'
import { isBedbank, isLEHotel } from 'lib/offer/offerTypes'

export function getPropertyIdFromURL(propertyNameAndId: string) {
  const position = Math.max(propertyNameAndId.lastIndexOf('le:'), propertyNameAndId.lastIndexOf('bedbank:'))
  return propertyNameAndId.slice(position)
}

interface Props extends RouteComponentProps<{ propertyNameAndId: string }> {
  bestOfferId?: string
  fetching: boolean
  propertyId: string
  offer: App.Offer | App.BedbankOffer | undefined
}

function PropertyPage(props: Props) {
  const { bestOfferId, fetching, propertyId, offer } = props
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchBestOfferForProperty(propertyId))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId])

  useEffect(() => {
    if (bestOfferId) {
      dispatch(fetchOfferById(bestOfferId))
    }
  }, [bestOfferId, dispatch])

  const offerType = useMemo(() => detectOfferTypeFromId(bestOfferId), [bestOfferId])

  if (fetching) {
    return <LayoutContainer>
      <LoadingIndicator inline>
        <BodyText variant="large" weight="bold">Searching for offers</BodyText>
      </LoadingIndicator>
    </LayoutContainer>
  } else if (!bestOfferId) {
    return <HttpStatus code={404}>
      <Error404Page />
    </HttpStatus>
  } else if (offerType === 'bedbank_hotel' && isBedbank(offer)) {
    const metaDescription = `Book the best deal for ${offer?.name}, ${offer?.property?.address.city} with Luxury Escapes today. The world's best escapes, handpicked by experts!`
    const canonicalTag = `/property/${offer?.slug}-bedbank:${offer?.id}`
    return <>
      {offer && <HeadData
        title={`${offer?.name}, ${offer?.property?.address.city} - ${config.title}`}
        description={metaDescription}
        imageId={offer.images[0]?.id || undefined}
        canonical={canonicalTag}
      />}
      <BedbankOfferPage propertyPageOfferId={bestOfferId} />
    </>
  }

  return <>
    {offer && isLEHotel(offer) && <OfferPageHeadData offer={offer} />}
    <OfferPage propertyPageOfferId={bestOfferId} />
  </>
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  const propertyId = getPropertyIdFromURL(ownProps.match.params.propertyNameAndId)
  const bestOfferState = state.offer.bestPropertyOffer[propertyId]
  const offer = bestOfferState?.offerId ? state.offer.offers[bestOfferState?.offerId] : undefined

  return {
    propertyId,
    bestOfferId: bestOfferState?.offerId,
    fetching: bestOfferState?.fetching ?? true,
    offer,
  }
}

export default connect(mapStateToProps)(withRouter(PropertyPage))
